// frontend/src/pages/SupplierCatalog.jsx
//
// The supplier catalog screen (opened as a workspace tab from the Suppliers
// grid's ⋯ menu). The ONLY place per-relation terms are edited — the per-item
// Suppliers dialog (components/supplier/ItemSuppliersDialog.jsx, opened from
// Item setup) is the item-side surface, and its "Edit terms" opens this one.
//
// Layout follows the house shape for a grid page: actions in the tile header
// (Save/Discard while dirty, then Assign items, Import, Export), filters in a row
// above the grid. The grid itself is SSRM over the supplier's assigned items,
// with inline editing + bulk save (the Default box included: a tick is an
// edit like any other, saved with the rest — supplierCatalogSave.js), and a
// per-row delete. ERP-owned stores lock every cell except the supplier cost
// and MOQ and disable add/delete/default (the backend gate is store-wide).
//
// TWO writes here can move an item's cost of record, and they resolve it
// differently:
//
//   Save — a price typed on the item's default row moves it with no
//     confirmation (the typed price is the answer). A Default tick elects
//     the relation through set-default, which always re-pegs the cost of
//     record to it (Model B), so it answers HTTP 200 `requires_confirmation`
//     with NO write when that would move a cost, and this page hosts the
//     shared <CurrencyChangeConfirmModal> to resolve it mid-save.
//     Consequences when editing it: a 2xx is NOT evidence anything was
//     written, and a retry that comes back still gated must never be
//     reported as success.
//
//   Assign items — the picker's bulk-create. It re-pegs only for a row that is
//     BOTH priced and the item's first relation, and raises no confirmation:
//     typing a price is the merchant saying which price the item uses. Every
//     other row writes the relation and leaves the valuation alone.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import {
  Button, Tooltip,
} from '@mui/material';
import PageBanner from '../components/common/PageBanner';
import { ICON, Plus, Trash } from '../icons';

import ImportButton from '../components/common/ImportButton';
import GridExportButton from '../components/common/GridExportButton';
import { GridEmptyState } from '../components/grid/NoRowsOverlay';
import GridIconAction from '../components/grid/GridIconAction';
import { ItemFilterBar, useItemFilter } from '../features/item-filter';

import Tile from '../components/layout/Tile';
import OverrideCellRenderer from '../components/grid/OverrideCellRenderer';
import ConfirmDialog from '../components/common/ConfirmDialog';
import SaveDiscardButtons from '../components/common/SaveDiscardButtons';
import CurrencyChangeConfirmModal from '../components/CurrencyChangeConfirmModal';
import BulkImportDialog from '../components/common/BulkImportDialog';
import SupplierCatalogImportOptions from '../components/supplier/SupplierCatalogImportOptions';
import { useAgGridTheme } from '../utils/agGridTheme';
import { useFormatters } from '../hooks/useFormatters';
import { effectiveCostLabel } from '../components/supplier/effectiveCost';
import { defaultColDef, defaultGridProps, editableGridProps, copyOnlyContextMenuItems, iconActionColumn, flexColumn } from '../utils/gridDefaults';
import { useColumnTypes } from '../hooks/useColumnTypes';
import { useGridState } from '../hooks/useGridState';
import { useSsrmGrid, buildExportQuery } from '../hooks/useSsrmGrid';
import { useEditableGridState } from '../hooks/useEditableGrid';
import useKeyboardSave from '../hooks/useKeyboardSave';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useResolutionGate } from '../hooks/useResolutionGate';
import { useWriteGate } from '../hooks/useWriteGate';
import {
  useSupplierCatalogItemBulkUpdate, useBulkCreateSupplierCatalogItems,
  useSetDefaultSupplierCatalogItem, useDeleteSupplierCatalogItem,
} from '../hooks/useSupplierCatalogItemMutations';
import ItemPickerDialog from '../components/common/ItemPickerDialog';
import { vendorDisplayName } from '../hooks/useSoR';
import useBulkImport from '../hooks/useBulkImport';
import { DATA } from '../queries/dataChanged';
import { useTabParams } from '../tabs/useTabParams';
import { decodeRouteSegment } from '../tabs/routeSegment';
import { useTabs } from '../tabs/useTabs';
import { useAuth } from '../contexts/AuthContext';
import { useIsShopify, productColumn } from '../components/grid/productIdentity';
import { apiJson, translateRequestError } from '../queries/api';
import { supplierCatalogItemLabel } from './supplierCatalogItemLabel';
import { assignResultAlert } from './supplierCatalogAssignResult';
import { suppliers as suppliersKey } from '../queries/keys';
import { failedRowsMessage } from '../utils/failedRows';
import { DEFAULT_FIELD, isDefaultTick, saveSupplierCatalogEdits } from './supplierCatalogSave';
import '../styles/pages.css';
import { showToast } from '../components/common/appToastBus';
import { GridPage, GridViewport } from '../components/grid/GridPage';

export default function SupplierCatalog() {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();
  const { id, itemCode } = useTabParams();
  const supplierId = id;
  const { setOwnTitle } = useTabs();
  const { selectedStore } = useAuth();
  const storeId = selectedStore?.id;
  const isShopify = useIsShopify();
  // Store-wide gate: when an ERP owns item-supplier links every relation on
  // this screen is ERP-owned — all cells read-only except cost and MOQ, and
  // add/delete/Default tick disabled (mirrors the backend _erp_owns_links gate,
  // which is store-level, not per-row). Locked too until the answer arrives.
  const { mayWrite: linksWritable, erpVendor } = useWriteGate('item_supplier_links');

  // How this screen NAMES a row's item in a dialog or toast — the grid's own
  // Product cell already follows the rule and these sentences have to as well.
  // The mapping itself lives in its own module so it can be tested without
  // mounting the page; see supplierCatalogItemLabel.js for why it is needed.
  const itemLabelOf = useCallback(
    (row) => supplierCatalogItemLabel(row, isShopify), [isShopify],
  );

  const gridTheme = useAgGridTheme();
  const columnTypes = useColumnTypes();
  const gridState = useGridState('supplier_catalog');
  const gridApiRef = useRef(null);
  const pageSize = 100;

  const { confirm, dialogProps } = useConfirmDialog();
  // The cost-of-record gate, hosted HERE rather than punting to another screen:
  // both writes on this page can elect an item's first default supplier.
  const costGate = useResolutionGate();
  // ── Tab title: "Supplier catalog — <supplier name>" once the supplier resolves ──
  const { data: supplier } = useQuery({
    queryKey: suppliersKey.detail(storeId, supplierId),
    queryFn: () => apiJson.get(`/api/suppliers/${supplierId}/`),
    enabled: !!supplierId,
  });
  const supplierName = supplier?.supplier_name || '';
  useEffect(() => {
    if (supplierName) setOwnTitle(`${t('supplierCatalog.titlePrefix')} — ${supplierName}`);
  }, [supplierName, setOwnTitle, t]);

  // ── Item filter (find rows among assigned items) ──
  // The item filter every item grid uses: typed text searches, a picked item
  // is that one item. A deep link (Data Health "Fix order rules", the item
  // Suppliers dialog's "Edit terms") arrives with one item's code in the route
  // (:itemCode), decoded through the shared segment reader (tabs/routeSegment).
  // Arrow indirection: guardUnsavedChanges is declared below (it needs the
  // editable-grid state) — the hook only invokes it, never during render.
  const itemFilter = useItemFilter({
    persistKey: 'catalogItemFilter',
    seed: decodeRouteSegment(itemCode),
    beforeChange: () => guardUnsavedChanges(),
  });

  const ssrmParams = useMemo(() => ({
    supplier: supplierId,
    ...itemFilter.params,
  }), [supplierId, itemFilter.params]);

  const { onGridReady, onGridPreDestroyed, refresh, pagination } = useSsrmGrid({
    endpoint: '/api/supplier-catalog-items/',
    params: ssrmParams,
    pageSize,
    onError: (err) => showToast(translateRequestError(err, t, 'common.fetchError'), 'error'),
    gridRef: gridApiRef,
    enabled: !!supplierId,
  });

  // ── Mutations ──
  const bulkUpdateMutation = useSupplierCatalogItemBulkUpdate();
  const bulkCreateMutation = useBulkCreateSupplierCatalogItems();
  const setDefaultMutation = useSetDefaultSupplierCatalogItem();
  const deleteMutation = useDeleteSupplierCatalogItem();

  // ── Export (top-right button → CSV/Excel menu) ──
  // Per-supplier export of the assigned items, honouring the grid filter — the
  // same file the import action round-trips onto another supplier.
  const exportProps = {
    endpoint: '/api/supplier-catalog-items/export/',
    filename: 'supplier_catalog',
    // supplier is the base scope (this screen is always one supplier's items);
    // the item filter is the only user filter, dropped for scope 'all'.
    getParams: useCallback(
      (scope) => buildExportQuery(scope === 'all' ? { supplier: supplierId } : ssrmParams),
      [supplierId, ssrmParams],
    ),
  };

  // ── Import (top-right button → dialog) ──
  // Append vs replace, and (appending only) whether an item already on the
  // list takes its details from the file. These ride the upload as options the
  // backend validates before it reads the file, so a bad value is refused
  // without leaving an audit row for a file nobody opened.
  const [importOpen, setImportOpen] = useState(false);
  const [importMode, setImportMode] = useState('append');
  const [importOverwrite, setImportOverwrite] = useState(false);
  const importExtraParams = useMemo(() => ({
    supplier_id: supplierId,
    mode: importMode,
    // Replace overwrites the whole list, so the conflict policy is moot there.
    on_conflict: importMode === 'append' && importOverwrite ? 'overwrite' : 'keep',
  }), [supplierId, importMode, importOverwrite]);
  const importHook = useBulkImport({
    modelName: 'supplier_catalog',
    endpoint: '/api/supplier-catalog-items/import/',
    templateEndpoint: '/api/supplier-catalog-items/template/',
    extraParams: importExtraParams,
    changes: [DATA.supplierLinks, DATA.items],
    // Replace is destructive and must not be sticky. Reset rides the hook's
    // own reset so there is a single close path, not a second one the page
    // has to remember to keep in step.
    onReset: () => {
      setImportMode('append');
      setImportOverwrite(false);
    },
  });
  const importCounters = useMemo(() => [
    { key: 'created', label: (n) => t('supplierCatalog.import.resultCreated', { count: n }) },
    { key: 'updated', label: (n) => t('supplierCatalog.import.resultUpdated', { count: n }) },
    { key: 'kept', label: (n) => t('supplierCatalog.import.resultKept', { count: n }) },
    { key: 'removed', label: (n) => t('supplierCatalog.import.resultRemoved', { count: n }) },
    { key: 'duplicates_collapsed', label: (n) => t('supplierCatalog.import.resultDuplicates', { count: n }) },
    { key: 'blank_identifier_rows', label: (n) => t('supplierCatalog.import.resultBlankRows', { count: n }) },
  ], [t]);

  // ── Bulk-save ──
  // The save flow is this page's own (useEditableGridState, not
  // useEditableGrid): the terms batch and the per-row elections run in an
  // order the cost of record depends on, and an election can stop at the
  // adopt dialog — see supplierCatalogSave.js.
  const editing = useEditableGridState({ getRowId: (row) => String(row.id) });
  const {
    gridContext, unsavedCellClass, recordCellChange, getChangedCellsRef,
    clearCellsByPredicate, clearAll: clearAllEdits,
  } = editing;
  const [saving, setSaving] = useState(false);

  const onCellValueChanged = useCallback((event) => {
    const fieldName = event.colDef.field;
    if (fieldName === DEFAULT_FIELD && event.newValue !== true) {
      // A pending tick taken back is no edit: the row is the server's again.
      // Nothing else reaches here unticked — the saved default is read-only.
      clearCellsByPredicate((info) => isDefaultTick(info) && String(info.rowId) === String(event.data.id));
    } else {
      recordCellChange({
        row: event.data, fieldName, oldValue: event.oldValue, newValue: event.newValue,
      });
    }
    event.api.refreshCells({ force: true, rowNodes: [event.node], columns: [fieldName] });
  }, [recordCellChange, clearCellsByPredicate]);

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const { savedTerms, savedTicks, failed } = await saveSupplierCatalogEdits(getChangedCellsRef(), {
        bulkUpdate: (payload) => bulkUpdateMutation.mutateAsync(payload),
        setDefault: (vars) => setDefaultMutation.mutateAsync(vars),
        requestGate: costGate.request,
        refusedMessage: (err) => translateRequestError(err, t, 'supplierCatalog.messages.setDefaultFailed'),
        stillGatedMessage: t('msg.saveFailed'),
      });
      // The written cells leave the queue; a refused or cancelled one stays
      // where the merchant typed it, blue, to retry or discard.
      clearCellsByPredicate((info) => (
        (isDefaultTick(info) ? savedTicks : savedTerms).has(String(info.rowId))
      ));
      const savedRows = new Set([...savedTerms, ...savedTicks]).size;
      if (failed.length > 0) {
        showToast(failedRowsMessage(t, failed), 'error');
      } else if (savedRows > 0) {
        showToast(t('supplierCatalog.messages.saveSuccess', { count: savedRows }), 'success');
      }
    } catch (err) {
      // The terms batch itself was refused or never reached: nothing written.
      console.error('SupplierCatalog save:', err.message);
      showToast(translateRequestError(err, t, 'msg.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  useKeyboardSave(handleSaveChanges, editing.isDirty && !saving && !costGate.isOpen);
  const unsavedGuard = useUnsavedChangesGuard(editing.isDirty);

  const handleDiscardChanges = () => {
    clearAllEdits();
    refresh({ purge: false });
    showToast(t('common.changesDiscarded'), 'info');
  };

  // A filter change re-reads the rows, so the edits typed into them go with it:
  // ask first, the way every editable grid does.
  const guardUnsavedChanges = async () => {
    if (!editing.isDirty) return true;
    const ok = await confirm({
      title: t('common.unsavedChangesTitle'),
      message: t('supplierCatalog.messages.unsavedChangesFilter'),
      confirmLabel: t('common.discard'),
      variant: 'warning',
    });
    if (!ok) return false;
    editing.clearAll();
    return true;
  };

  // ── Assign items (header button → picker modal) ──
  // The picker lists items NOT yet on this supplier (?exclude_supplier=) and
  // only active ones, and the merchant may price a row before adding it.
  //
  // Pricing a row whose item has no supplier yet is what makes that price the
  // item's cost of record and pushes it to the sales channel — the relation
  // becomes the item's source, so under Model B its valuation follows. Leaving
  // the cost blank, or adding to an item that already sources elsewhere, writes
  // the relation only. The endpoint owns that rule; this page just collects the
  // typed prices.
  const [addOpen, setAddOpen] = useState(false);
  const pickerParams = useMemo(() => ({
    status: 'active',
    exclude_supplier: supplierId,
  }), [supplierId]);

  const pickerColumns = useMemo(() => [
    {
      field: 'cost',
      headerName: t('supplierCatalog.columns.supplierCost'),
      headerTooltip: t('supplierCatalog.tooltips.supplierCost'),
      // decimalColumn already renders an em-dash for null and parses a cleared
      // cell back to null, so an unpriced row says so rather than reading 0.00.
      type: 'decimalColumn',
      editable: true,
      // What the merchant types here exists only in this dialog — /api/items/
      // has no `cost` to order by and answers invalid_sort, which clears the
      // sort, so a sortable header would be a click that does nothing.
      sortable: false,
    },
  ], [t]);

  const handleAdd = useCallback(async ({ payload, edits, query }) => {
    // Only what was actually typed. An item with no entry gets a relation with
    // no price — a real state (this supplier's price is not known yet), not a
    // missing one to substitute a zero for.
    const costs = {};
    for (const [rowId, fields] of Object.entries(edits || {})) {
      if (fields.cost != null && fields.cost !== '') costs[rowId] = fields.cost;
    }
    try {
      const data = await bulkCreateMutation.mutateAsync({
        query,
        body: { supplier_id: supplierId, ...payload, costs },
      });
      setAddOpen(false);
      const { message, severity } = assignResultAlert(data, t);
      showToast(message, severity);
    } catch (error) {
      showToast(translateRequestError(error, t, 'supplierCatalog.messages.assignFailed'), 'error');
    }
  }, [bulkCreateMutation, supplierId, t]);

  // ── Per-row delete ──
  const handleDelete = useCallback(async (row) => {
    const ok = await confirm({
      title: t('supplierCatalog.delete.title'),
      // Deleting the item's default relation leaves it with no default
      // supplier — call that out explicitly.
      message: row.is_default
        ? t('supplierCatalog.delete.messageDefault', { item: itemLabelOf(row) })
        : t('supplierCatalog.delete.message', { item: itemLabelOf(row) }),
      confirmLabel: t('common.delete'),
      variant: 'danger',
    });
    if (!ok) return;
    try {
      // `promoted_default` names the supplier auto-promoted to default (the
      // sole survivor of removing the item's default), or is null.
      const result = await deleteMutation.mutateAsync({ id: row.id });
      const promoted = result?.promoted_default;
      showToast(
        promoted
          ? t('supplierCatalog.messages.deleteSuccessPromoted', {
              item: itemLabelOf(row), supplier: promoted.supplier_name })
          : t('supplierCatalog.messages.deleteSuccess', { item: itemLabelOf(row) }),
        'success',
      );
    } catch (error) {
      showToast(translateRequestError(error, t, 'supplierCatalog.messages.deleteFailed'), 'error');
    }
  }, [confirm, t, deleteMutation, itemLabelOf]);

  // ── Columns ──
  const columnDefs = useMemo(() => {
    // Editable only once the store's links are known not to be ERP-owned. Cost
    // and MOQ are the exceptions — merchant-editable even on ERP stores (both
    // round-trip to the ERP relation).
    const merchantEditable = linksWritable;
    return [
      // Shopify: merchant-facing Product cell (image + name). The item lives in
      // a nested `item` dict, so image/code resolve via dot-paths; colId stays
      // 'item_name' to preserve the existing SSRM sort binding. Non-Shopify
      // (ERP) keeps the item_code + item_name columns — item_code is real there.
      ...(isShopify
        ? [productColumn({
            headerName: t('supplierCatalog.columns.product'),
            colId: 'item_name',
            valueGetter: (p) => p.data.item.name,
            nameField: 'item.name',
            imageField: 'item.image_url',
            codeField: 'item.code',
            skuField: 'item.sku',
            minWidth: 200,
            ...flexColumn(2),
          })]
        : [
            {
              colId: 'item_code',
              headerName: t('supplierCatalog.columns.itemCode'),
              valueGetter: (p) => p.data.item.code,
              editable: false,
              minWidth: 130,
              ...flexColumn(),
            },
            {
              colId: 'item_name',
              headerName: t('supplierCatalog.columns.itemName'),
              valueGetter: (p) => p.data.item.name,
              editable: false,
              minWidth: 180,
              ...flexColumn(2),
            },
          ]),
      {
        field: 'supplier_sku',
        headerName: t('supplierCatalog.columns.supplierSku'),
        headerTooltip: t('supplierCatalog.tooltips.supplierSku'),
        ...flexColumn(),
        editable: merchantEditable,
      },
      {
        // The supplier's OWN price, NULL = none. The relation still has a price
        // then — the next column shows it.
        field: 'cost',
        headerName: t('supplierCatalog.columns.supplierCost'),
        headerTooltip: t('supplierCatalog.tooltips.supplierCost'),
        type: 'decimalColumn',
        editable: true,   // Cost stays editable even on ERP stores (round-trips).
      },
      {
        // What this relation really charges: the own price, or the item's cost
        // of record converted into the supplier's currency when there is none.
        // Read-only — the merchant changes it by
        // typing an own price, or on Item setup. The server prices each page
        // after the query, so it cannot order by it: `sortable: false` keeps
        // the header from offering a sort the server would refuse.
        field: 'effective_cost',
        headerName: t('supplierCatalog.columns.effectiveCost'),
        headerTooltip: t('supplierCatalog.tooltips.effectiveCost'),
        type: 'decimalColumn',
        editable: false,
        sortable: false,
        valueFormatter: (p) => (
          p.value == null ? '—' : effectiveCostLabel(p.data, { formatNumber })
        ),
      },
      {
        // Display-only: relation currency (editing currency is API-only —
        // it follows the supplier default / ERP). The colId is the ORM path
        // the endpoint sorts by: `currency` is a nested object on the wire,
        // which the sort whitelist cannot order.
        colId: 'currency__CurrencyCode',
        headerName: t('supplierCatalog.columns.currency'),
        valueGetter: (p) => p.data.currency?.currency_code ?? '',
        editable: false,
      },
      {
        field: 'lead_time_days',
        headerName: t('supplierCatalog.columns.leadTime') + ` (${t('common.days')})`,
        headerTooltip: t('supplierCatalog.tooltips.leadTime'),
        type: 'numberColumn',
        editable: merchantEditable,
        // Lead time is a two-tier value: the supplier's SLA (SupplierSetup) is the
        // default, and this per-relation field is an OPTIONAL override. NULL means
        // "inherit the supplier's lead time" — so render the inherited SLA in muted
        // style (never a bare 0) and mark an explicit override in the user ink.
        // Clearing the cell writes NULL (numberColumn valueParser) → back to inherit.
        cellRenderer: OverrideCellRenderer,
        cellRendererParams: {
          getSystemValue: (data) => data.supplier_sla_lead_time,
          isModified: (data) => data.lead_time_days != null,
        },
      },
      {
        field: 'moq',
        headerName: t('supplierCatalog.columns.moq'),
        type: 'numberColumn',
        editable: true,   // MOQ stays editable even on ERP stores (round-trips).
      },
      {
        field: 'max_oq',
        headerName: t('supplierCatalog.columns.maxOq'),
        type: 'numberColumn',
        editable: merchantEditable,
      },
      {
        field: 'order_multiple',
        headerName: t('supplierCatalog.columns.orderMultiple'),
        headerTooltip: t('supplierCatalog.tooltips.orderMultiple'),
        type: 'numberColumn',
        editable: merchantEditable,
      },
      {
        // Ticking an unchecked row queues that relation as the item's default
        // supplier, saved with the other edits. The saved default is checked
        // and read-only: a default can't be un-set, only replaced by choosing
        // another. A pending tick can still be taken back.
        field: DEFAULT_FIELD,
        headerName: t('supplierCatalog.columns.default'),
        headerTooltip: t('common.tooltips.defaultSupplier'),
        type: 'booleanColumn',
        editable: (params) => linksWritable && (!params.data.is_default || unsavedCellClass(params)),
      },
      iconActionColumn({
        colId: 'delete',
        cellRenderer: GridIconAction,
        cellRendererParams: {
          icon: Trash,
          label: t('common.delete'),
          onClick: handleDelete,
          disabled: !linksWritable,
          destructive: true,
        },
      }),
    ];
  }, [t, linksWritable, unsavedCellClass, handleDelete, isShopify, formatNumber]);

  const getRowId = useCallback((params) => String(params.data.id), []);

  return (
    <div className="page-container">
      {unsavedGuard}
      <div className="tiles-row">
        <Tile
          width="full"
          title={supplierName
            ? `${t('supplierCatalog.titlePrefix')} — ${supplierName}`
            : t('supplierCatalog.titlePrefix')}
          toolbar={
            <>
              {/* Disabled rather than hidden under an ERP: a missing button
                  reads as a missing feature, and the merchant needs to know
                  the links are managed elsewhere and WHERE. The tooltip wraps a
                  span because MUI does not fire pointer events on a disabled
                  button. */}
              <Tooltip
                title={erpVendor
                  ? t('supplierCatalog.assign.erpManagedTooltip', {
                      vendor: vendorDisplayName(erpVendor) })
                  : ''}
              >
                <span>
                  <Button
                    variant="contained"
                    startIcon={<Plus size={ICON.md} />}
                    onClick={() => setAddOpen(true)}
                    disabled={!linksWritable}
                  >
                    {t('supplierCatalog.assign.button')}
                  </Button>
                </span>
              </Tooltip>
            </>
          }
          editActions={
            <SaveDiscardButtons
              count={editing.changedRows.length}
              saving={saving}
              onSave={handleSaveChanges}
              onDiscard={handleDiscardChanges}
            />
          }
          headerActions={
            <>
              {linksWritable && (
                <ImportButton
                  entityKey="supplier_catalog"
                  onClick={() => setImportOpen(true)}
                />
              )}
              <GridExportButton {...exportProps} />
            </>
          }
        >
          <GridPage>
            <PageBanner severity="info" messages={[erpVendor && t('supplierCatalog.erpManagedNote')]} />

            {/* Filter row — the house shape for a grid page: filters in the
                body, actions in the header. */}
            <ItemFilterBar filter={itemFilter} />

            <GridViewport>
              {/* The "use Assign items" instruction only while that button
                  works; under an ERP the note above already says where the
                  links are made. */}
              {gridTheme && (
                <GridEmptyState
                  title={t(linksWritable ? 'supplierCatalog.empty' : 'supplierCatalog.emptyReadOnly')}
                  filtersActive={!!itemFilter.filter}
                >
                  <AgGridReact
                    {...defaultGridProps}
                    {...editableGridProps}
                    context={gridContext}
                    theme={gridTheme}
                    {...gridState}
                    columnDefs={columnDefs}
                    getRowId={getRowId}
                    defaultColDef={defaultColDef}
                    columnTypes={columnTypes}
                    onCellValueChanged={onCellValueChanged}
                    onGridReady={onGridReady}
                    onGridPreDestroyed={onGridPreDestroyed}
                    rowModelType="serverSide"
                    serverSideSortAllLevels={true}
                    {...pagination}
                    getContextMenuItems={copyOnlyContextMenuItems}
                  />
                </GridEmptyState>
              )}
            </GridViewport>
          </GridPage>
        </Tile>
      </div>

      <ConfirmDialog {...dialogProps} />
      <CurrencyChangeConfirmModal {...costGate.modalProps} />
      <ItemPickerDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title={t('supplierCatalog.assign.dialogTitle')}
        subtitle={supplierName}
        endpoint="/api/items/"
        params={pickerParams}
        gridStateKey="supplier_catalog_add"
        extraColumns={pickerColumns}
        skuField="shopify_sku"
        onConfirm={handleAdd}
        confirming={bulkCreateMutation.isPending}
        emptyMessage={t('supplierCatalog.assign.noMatches')}
      />
      <BulkImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        importHook={importHook}
        entityKey="supplier_catalog"
        title={t('supplierCatalog.import.title')}
        subtitle={supplierName}
        prepareHint={t('supplierCatalog.import.prepareHint')}
        counters={importCounters}
        options={(
          <SupplierCatalogImportOptions
            mode={importMode}
            onModeChange={setImportMode}
            overwrite={importOverwrite}
            onOverwriteChange={setImportOverwrite}
            supplierName={supplierName}
            disabled={importHook.loading}
          />
        )}
      />
    </div>
  );
}
